import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, CheckCircle, XCircle, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWhatsAppTest() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('Sanjaya Admin');
  const [campaign, setCampaign] = useState('');
  const [params, setParams] = useState(['']);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const authH = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/whatsapp/status`, authH);
      setStatus(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not fetch status', variant: 'destructive' });
    }
  };

  const loadLogs = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/whatsapp/logs`, authH);
      setLogs(r.data.logs || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadStatus(); loadLogs(); /* eslint-disable-next-line */ }, []);

  const sendTest = async () => {
    if (!phone.trim() || !campaign.trim()) {
      toast({ title: 'Missing info', description: 'Phone and campaign name required', variant: 'destructive' });
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const cleanParams = params.map(p => p.trim()).filter(Boolean);
      const r = await axios.post(`${API_URL}/api/admin/whatsapp/test-send`, {
        phone: phone.trim(),
        name: name.trim() || 'Sanjaya Admin',
        campaign_name: campaign.trim(),
        template_params: cleanParams,
      }, authH);
      setLastResult(r.data);
      toast({
        title: r.data.success ? 'Sent' : 'AiSensy rejected',
        description: r.data.success ? 'Message dispatched to AiSensy' : 'See response details below',
        variant: r.data.success ? 'default' : 'destructive',
      });
      loadLogs();
    } catch (e) {
      setLastResult({ success: false, error: e?.response?.data?.detail || e.message });
      toast({ title: 'Failed', description: 'Request failed', variant: 'destructive' });
    } finally { setSending(false); }
  };

  if (!status) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-root">
      {/* Status card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            WhatsApp Integration (AiSensy)
          </CardTitle>
          <CardDescription>Test your AiSensy connection and approved campaign templates.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs uppercase">Connection</p>
              <p className={`mt-1 font-semibold ${status.configured ? 'text-emerald-600' : 'text-red-600'}`}>
                {status.configured ? '✓ API key configured' : '✗ Missing AISENSY_API_KEY'}
              </p>
              {status.api_key_preview && <p className="text-xs text-gray-400 font-mono mt-0.5">{status.api_key_preview}</p>}
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Admin Alert Number</p>
              <p className="mt-1 font-semibold">{status.ops_manager_phone || <span className="text-amber-600">Not set</span>}</p>
              <p className="text-xs text-gray-400">Set OPS_MANAGER_PHONE in backend/.env</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">API Endpoint</p>
              <p className="mt-1 font-mono text-xs text-gray-600 break-all">{status.api_url}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Send test panel */}
      <Card>
        <CardHeader>
          <CardTitle>Send a Test Message</CardTitle>
          <CardDescription>
            Pick any AiSensy campaign you've created and approved. The campaign name must match <em>exactly</em>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Recipient phone</Label>
              <Input
                placeholder="9920687945  or  919920687945  or  +91 9920687945"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                data-testid="wa-test-phone"
              />
              <p className="text-xs text-gray-400 mt-1">Any format — backend normalizes to E.164.</p>
            </div>
            <div>
              <Label>Recipient name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} data-testid="wa-test-name" />
            </div>
          </div>

          <div>
            <Label>AiSensy campaign name</Label>
            <Input
              placeholder="e.g. sanjaya_system_alert"
              value={campaign}
              onChange={e => setCampaign(e.target.value)}
              data-testid="wa-test-campaign"
              className="font-mono"
            />
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="text-xs text-gray-500">Quick fill:</span>
              {Object.entries(status.campaign_slugs || {}).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setCampaign(v)}
                  className="text-xs px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 font-mono"
                  data-testid={`wa-quick-${k}`}
                >{v}</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Template variables (in order — {`{{1}}, {{2}}, …`})</Label>
            <div className="space-y-2 mt-1">
              {params.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-xs font-mono bg-gray-100 px-2 py-2 rounded w-12 text-center">{`{{${i+1}}}`}</span>
                  <Input
                    value={p}
                    placeholder={`Value for variable ${i+1}`}
                    onChange={e => setParams(params.map((x, idx) => idx === i ? e.target.value : x))}
                    data-testid={`wa-test-param-${i}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => setParams(params.filter((_, idx) => idx !== i))}>
                    <Trash2 className="w-4 h-4 text-gray-400" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setParams([...params, ''])} data-testid="wa-add-param">
                <Plus className="w-4 h-4 mr-1" /> Add variable
              </Button>
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={sendTest} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-send-test">
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending…' : 'Send Test Message'}
            </Button>
          </div>

          {lastResult && (
            <div className={`mt-4 p-4 rounded border ${lastResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="wa-result">
              <div className="flex items-center gap-2 font-semibold">
                {lastResult.success
                  ? <><CheckCircle className="w-4 h-4 text-emerald-600" /> AiSensy accepted the request</>
                  : <><XCircle className="w-4 h-4 text-red-600" /> AiSensy rejected the request</>}
              </div>
              <pre className="mt-2 text-xs font-mono whitespace-pre-wrap break-all text-gray-700">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
              {!lastResult.success && (
                <div className="mt-3 text-xs text-gray-600 space-y-1">
                  <p className="font-semibold">Common fixes:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li><strong>"Campaign not found"</strong> → that exact campaign name doesn't exist in your AiSensy dashboard. Go to <a href="https://app.aisensy.com" className="text-blue-600 underline" target="_blank" rel="noreferrer">app.aisensy.com → Campaigns</a> and verify it.</li>
                    <li><strong>"Template not approved"</strong> → Meta hasn't approved the template yet. Approval typically takes 1–24 hrs.</li>
                    <li><strong>"Invalid phone"</strong> → Recipient hasn't messaged your business in the last 24 hrs and the campaign is a marketing template (not utility). Ask them to send any message to your business number once, then retry.</li>
                    <li><strong>"Daily limit reached"</strong> → Your AiSensy FREE plan allows ~50-100 conversations/day. Upgrade or wait.</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent WhatsApp attempts</CardTitle>
            <CardDescription className="text-xs">Last 25 messages dispatched via AiSensy.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadLogs}><RefreshCw className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="px-0">
          {logs.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">No attempts yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">When</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Recipient</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Campaign</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-b">
                    <td className="px-3 py-2 text-xs text-gray-500">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.recipient}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.campaign}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${l.result?.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {l.result?.success ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
